import { startCase } from 'lodash-es'
import { useContext, useRef } from 'react'
import { helperLeadProperties, HelperLeadProperty, TemplateRoute, TemplateRouting } from 'types'
import { sleep } from 'utils'
import { makeSelection } from '../../utils/selectionUtils'
import {
  getTrait,
  StepPosition,
  TraitWithSelections,
  useRemainingRouting,
} from '../../utils/traitUtils'
import { SessionContext, useExplorerConfig } from '../providers'
import { SDropdown, SText, SView } from '../scaled'
import AnimatedPanel from './AnimatedPanel'

export default function InputTraitSteps() {
  const { templateDocs } = useExplorerConfig()
  const { getLanguageValue, routeSteps, session } = useContext(SessionContext)

  const selectGroups = useRef<TraitWithSelections[]>([])
  const position = useRef<StepPosition>('pending')
  const routeStepCount = useRef(0)
  const currentStepKey = routeSteps[routeSteps.length - 1]?.key
  const currentGroup = getTrait(templateDocs!, currentStepKey)
  if (routeSteps.length === 0) position.current = 'pending'
  if (currentGroup?.displayType === 'select') {
    position.current = 'current'
    selectGroups.current = makeSelectGroups()
  } else {
    if (position.current === 'current')
      position.current = routeStepCount.current < routeSteps.length ? 'complete' : 'pending'
    sleep(500).then(() => (selectGroups.current = []))
  }
  routeStepCount.current = routeSteps.length

  function makeSelectGroups() {
    let groups: TraitWithSelections[] = []
    let steps = [...routeSteps]

    while (true) {
      let step = steps.pop()
      if (!step) break

      const group = getTrait(templateDocs!, step.key)
      if (!group || group.displayType !== 'select') break

      const selected =
        session?.traits?.filter(s => {
          if (s.groupTitle !== getLanguageValue(group.title)) return false
          if (s.traitId) return group.values?.some(v => v.id === s.traitId)
          else return group.values?.some(v => getLanguageValue(v.title) === s.title)
        }) || []

      groups.unshift({ ...group, selected, step })
    }

    return groups
  }

  return (
    <AnimatedPanel position={position.current}>
      <SView flex={1} gap={30}>
        {selectGroups.current.map((props, index) => (
          <InputTrait {...props} disabled={index !== selectGroups.current.length - 1} key={index} />
        ))}
      </SView>
    </AnimatedPanel>
  )
}

type Disabled = {
  disabled?: boolean
}
type OnDone = { onDone: (route: TemplateRoute) => void }

function InputTrait(props: TraitWithSelections & Disabled) {
  const { leadField, title, titleDisplay } = props
  const { templateDocs } = useExplorerConfig()
  const { getLanguageValue, routeSteps, updateRouteSteps } = useContext(SessionContext)

  function handleDone(routing: TemplateRouting) {
    const { key, type } = routing
    if (type !== 'trait') return

    const nextGroup = getTrait(templateDocs!, key)
    if (nextGroup?.displayType !== 'select') return

    updateRouteSteps([...routeSteps, { key, type }])
  }

  return (
    <SView h={100} gap={10}>
      <SText fontSize={30}>{getLanguageValue(titleDisplay || title) || startCase(leadField)}</SText>
      {leadField ? (
        <LeadField {...props} onDone={handleDone} />
      ) : (
        <TraitSelect {...props} onDone={handleDone} />
      )}
    </SView>
  )
}

function LeadField({
  disabled,
  leadField,
  onDone,
  values,
}: TraitWithSelections & OnDone & Disabled) {
  const { getLanguageValue, mergeSession, session } = useContext(SessionContext)

  const remainingRouting = useRemainingRouting()
  const isBoolean = leadField === 'followUp' || leadField === 'optIn'
  const rawValue =
    session?.lead?.[leadField as HelperLeadProperty] ?? session?.lead?.custom?.[leadField!]

  function handleSelect(value: string | boolean) {
    const isCustom = !helperLeadProperties.includes(leadField as HelperLeadProperty)
    const lead = isCustom ? { custom: { [leadField!]: value.toString() } } : { [leadField!]: value }
    mergeSession({ lead })
    onDone(remainingRouting!.default!)
  }

  if (isBoolean) {
    const value = rawValue ? 'yes' : rawValue === false ? 'no' : null

    return (
      <SDropdown
        disabled={disabled}
        onChange={value => handleSelect(value === 'yes' ? true : false)}
        options={[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ]}
        placeholder="Select an item"
        value={value || undefined}
        w={200}
      />
    )
  } else {
    const options =
      values?.map(v => {
        const label = getLanguageValue(v.title)
        return { label, value: label }
      }) || []

    return (
      <SDropdown
        disabled={disabled}
        onChange={handleSelect}
        options={options}
        placeholder="Select an item"
        value={rawValue?.toString()}
        w={200}
      />
    )
  }
}

function TraitSelect({
  disabled,
  values,
  title,
  selected,
  onDone,
}: TraitWithSelections & OnDone & Disabled) {
  const { getLanguageValue, mergeSession, session } = useContext(SessionContext)
  const remainingRouting = useRemainingRouting()

  const options =
    values?.map(v => {
      const label = getLanguageValue(v.title)
      return { label, value: label }
    }) || []

  function handleSelect(value: string | null) {
    const traitValue = values?.find(v => getLanguageValue(v.title) === value)
    if (!traitValue) return

    const selection = makeSelection(traitValue, title!, getLanguageValue)
    const traits = [
      ...(session?.traits || []).filter(s => s.groupTitle !== getLanguageValue(title)),
      selection,
    ]
    mergeSession({ traits })
    const valueRoute = remainingRouting?.valueRoutes?.find(r => r.fromIds?.includes(traitValue.id!))
    onDone(valueRoute?.to || remainingRouting!.default!)
  }

  return (
    <SDropdown
      disabled={disabled}
      onChange={handleSelect}
      options={options}
      placeholder="Select an item"
      value={selected?.[0]?.title}
      w={200}
    />
  )
}
